import { useSyncExternalStore } from 'react';
import type { Permission } from './access';

export type RecheckStatus = 'idle' | 'checking' | 'failed';

export interface AccessRecheckState {
  // Views a server refused in this tab. Held until reload or a change of person: a refused view
  // never reads again, so a refusal cannot turn into a loop of 403s and re-reads of /me.
  refusedViews: ReadonlySet<Permission>;
  // The re-read of /me a refusal started: 'failed' outranks the session it would have replaced.
  recheck: RecheckStatus;
}

const INITIAL: AccessRecheckState = { refusedViews: new Set(), recheck: 'idle' };

let state: AccessRecheckState = INITIAL;
// One re-read of /me, identified by its ticket: the session it started in and, by reference, the run.
export interface RecheckTicket {
  readonly generation: number;
}

let activeView: Permission | null = null;
let inFlight: { ticket: RecheckTicket; run: Promise<void> } | null = null;
// Bumped by a reset, so a re-read still in flight from the previous person settles nothing.
let generation = 0;
const listeners = new Set<() => void>();

function publish(next: AccessRecheckState): void {
  state = next;
  for (const listener of listeners) listener();
}

export function subscribeAccessRecheck(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function getAccessRecheck(): AccessRecheckState {
  return state;
}

export function markViewRefused(view: Permission): boolean {
  if (state.refusedViews.has(view)) return false;
  publish({ ...state, refusedViews: new Set([...state.refusedViews, view]) });
  return true;
}

export function setRecheckStatus(recheck: RecheckStatus): void {
  if (state.recheck !== recheck) publish({ ...state, recheck });
}

// The view on screen. A write records it when it is sent and a read when its fetch starts, so a
// shared-code refusal names the view that asked, since the panel never reads `detail`.
export function registerActiveView(view: Permission): () => void {
  activeView = view;
  return () => {
    if (activeView === view) activeView = null;
  };
}

export function getActiveView(): Permission | null {
  return activeView;
}

// The running re-read, so a caller can await it; a refusal joins it through isRecheckRunning.
export function getRecheckInFlight(): Promise<void> | null {
  return inFlight?.run ?? null;
}

export function isRecheckRunning(): boolean {
  return inFlight !== null;
}

function release(ticket: RecheckTicket): void {
  // A newer re-read, or the next person's, is never cleared by an older one.
  if (inFlight?.ticket === ticket) inFlight = null;
}

// Starts a re-read: marks it checking, then runs `read` and publishes the run before returning, so a
// refusal for another view arriving meanwhile joins it. A run that ends without settling (its callback
// threw) is released all the same.
export function beginRecheck(read: (ticket: RecheckTicket) => Promise<void>): Promise<void> {
  const ticket: RecheckTicket = { generation };
  setRecheckStatus('checking');
  const run = read(ticket).finally(() => {
    release(ticket);
  });
  inFlight = { ticket, run };
  return run;
}

// Ends a re-read with the outcome `judge` returns. A run from before a reset is about a person who is
// no longer here: it judges and publishes nothing. Otherwise the run is released before the outcome is
// published, so a refusal handled the moment the outcome lands starts its own re-read rather than
// joining one that judged /me before that refusal existed.
export function settleRecheck(ticket: RecheckTicket, judge: () => RecheckStatus): void {
  if (ticket.generation !== generation) return;
  const outcome = judge();
  release(ticket);
  setRecheckStatus(outcome);
}

export function getRecheckGeneration(): number {
  return generation;
}

// Part of the session reset: a reload loses module state anyway, a change of person must too.
export function resetAccessRecheck(): void {
  generation += 1;
  inFlight = null;
  publish(INITIAL);
}

export function useAccessRecheck(): AccessRecheckState {
  return useSyncExternalStore(subscribeAccessRecheck, getAccessRecheck, getAccessRecheck);
}
